/**
 * Admin: Link Preview (Social / Share) settings.
 * Controls default og:image, author, and publish date used when the site URL is shared
 * (e.g. LinkedIn, Facebook, Twitter). Per-page overrides remain in SEO.
 */

import { useState, useEffect, useCallback } from 'react';
import { Share2, Save, Loader2, Image as ImageIcon, User, Calendar, AlertTriangle } from 'lucide-react';
import {
  getLinkPreviewSettings,
  saveLinkPreviewSettings,
  type LinkPreviewSettings,
} from '@/lib/cms';

interface AdminLinkPreviewProps {
  currentUserId: string;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  /** Optional action (e.g. Help) to show in the title row. */
  titleAction?: React.ReactNode;
}

const SITE_URL = 'https://smbprime.ai';
const DEFAULT_OG_IMAGE = `${SITE_URL}/social-share-smbprime.jpeg`;

export function AdminLinkPreview({
  currentUserId,
  showNotify,
  titleAction,
}: AdminLinkPreviewProps): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ogImage, setOgImage] = useState('');
  const [author, setAuthor] = useState('');
  const [publishDate, setPublishDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await getLinkPreviewSettings();
      if (settings) {
        setOgImage(settings.ogImage ?? '');
        setAuthor(settings.author ?? '');
        setPublishDate(settings.publishDate ?? '');
      } else {
        setOgImage(DEFAULT_OG_IMAGE);
        setAuthor('SMBPrime');
        setPublishDate('');
      }
    } catch (err) {
      console.error('[AdminLinkPreview] Failed to load link preview settings:', err);
      showNotify?.('Failed to load link preview settings', 'error');
      setOgImage(DEFAULT_OG_IMAGE);
      setAuthor('SMBPrime');
      setPublishDate('');
    } finally {
      setLoading(false);
    }
  }, [showNotify]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data: Partial<Omit<LinkPreviewSettings, 'updatedAt' | 'updatedBy'>> = {
        ogImage: ogImage.trim() || null,
        author: author.trim() || null,
        publishDate: publishDate.trim() || null,
      };
      await saveLinkPreviewSettings(data, currentUserId);
      showNotify?.(
        'Link preview settings saved. Developer action required: run `npm run seo:generate-index`, then `npm run build`, then `firebase deploy --only hosting`. After deploy, refresh cache using LinkedIn Post Inspector.',
        'success'
      );
    } catch (err) {
      console.error('[AdminLinkPreview] Failed to save link preview settings:', err);
      showNotify?.('Failed to save link preview settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Share2 className="text-blue-600" size={28} />
            <h1 className="text-3xl font-bold text-gray-900">Link Preview</h1>
          </div>
          <p className="text-gray-600 max-w-2xl">
            Set default metadata used when your site URL is shared on LinkedIn, Facebook, Twitter, and other platforms.
            These values appear in the &quot;Metadata that we gathered about this page&quot; section. Per-page overrides are managed in <strong>SEO</strong>.
          </p>
        </div>
        {titleAction}
      </div>

      <div className="overflow-hidden rounded-xl border-2 border-amber-300 bg-amber-50 shadow-sm">
        <div className="flex items-start gap-3 border-b border-amber-200 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Developer Publish Required</p>
            <p className="mt-0.5 text-xs text-amber-800">
              Link preview updates are saved to the database, but social crawlers read deployed static HTML. A developer must publish changes after every update.
            </p>
          </div>
        </div>
        <div className="space-y-2 px-4 py-3">
          <ol className="list-decimal space-y-1 pl-5 text-sm text-amber-900">
            <li>Save your Link Preview changes here.</li>
            <li>Ask developer to run <code className="rounded bg-amber-100 px-1 py-0.5">npm run seo:generate-index</code>.</li>
            <li>Then run <code className="rounded bg-amber-100 px-1 py-0.5">npm run build</code>.</li>
            <li>Then deploy with <code className="rounded bg-amber-100 px-1 py-0.5">firebase deploy --only hosting</code>.</li>
            <li>Use LinkedIn Post Inspector to refresh cache and verify preview.</li>
          </ol>
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href="https://www.linkedin.com/post-inspector/inspect/https:%2F%2Fsmbprime.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 shadow-sm hover:bg-amber-100"
            >
              Open LinkedIn Post Inspector
            </a>
            <a
              href="https://developers.facebook.com/tools/debug/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 shadow-sm hover:bg-amber-100"
            >
              Open Facebook Sharing Debugger
            </a>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-6 pt-5 pb-4 border-b border-gray-100 bg-blue-50/40">
          <div className="flex items-center gap-2 mb-1">
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-100 text-blue-600">
              <ImageIcon size={14} />
            </span>
            <h2 className="text-base font-bold text-gray-900">Default share image (og:image)</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Provide a metadata tag for the <code className="rounded bg-gray-200 px-1">og:image</code> in the page&apos;s head. Use an absolute URL (e.g. {SITE_URL}/social-share-smbprime.jpeg). Recommended size: 1200×630.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="linkpreview-ogimage" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Image URL
            </label>
            <input
              id="linkpreview-ogimage"
              type="url"
              value={ogImage}
              onChange={(e) => setOgImage(e.target.value)}
              placeholder={`${SITE_URL}/social-share-smbprime.jpeg`}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-6 pt-5 pb-4 border-b border-gray-100 bg-blue-50/40">
          <div className="flex items-center gap-2 mb-1">
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-100 text-blue-600">
              <User size={14} />
            </span>
            <h2 className="text-base font-bold text-gray-900">Author</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Provide a metadata tag for the author in the page&apos;s head, e.g. <code className="rounded bg-gray-200 px-1">&lt;meta name=&quot;author&quot; content=&quot;[Author name]&quot;&gt;</code>
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="linkpreview-author" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Author name
            </label>
            <input
              id="linkpreview-author"
              type="text"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="SMBPrime"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-6 pt-5 pb-4 border-b border-gray-100 bg-blue-50/40">
          <div className="flex items-center gap-2 mb-1">
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-100 text-blue-600">
              <Calendar size={14} />
            </span>
            <h2 className="text-base font-bold text-gray-900">Publish date</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Optional. Some platforms show article publication date. Use ISO format (YYYY-MM-DD).
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="linkpreview-publishdate" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Publish date
            </label>
            <input
              id="linkpreview-publishdate"
              type="text"
              value={publishDate}
              onChange={(e) => setPublishDate(e.target.value)}
              placeholder="2026-01-01"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
